import { keyframes, styled } from '@mui/material/styles';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
  Popover,
  Checkbox,
  FormControlLabel,
  Alert,
} from '@mui/material';
import { thinScrollbar } from '../../../../../constants'

// --- CENTRALIZED COLOR SCHEMES FOR VIEW, FILTER & SLICE ---
export const PANEL_COLORS = {
  view: {
    primary: '#4f46e5',
    primaryDark: '#3730a3',
    primaryLight: '#c7d2fe',
    bg: '#eef2ff',
    bgSummary: '#dde5ff',
    borderSummary: '#5778ff',
    colorSummary: '#4338ca',
    accent: 'rgba(79, 70, 229, 0.6)',
    closeIcon: '#5953B0',
    svgFill: '#4F46E5',
    svgStroke: '#4F46E5',
  },
  filter: {
    primary: '#102554',
    primaryLight: '#a5b4fc',
    bg: '#eef2ff',
    borderSummary: 'rgba(30, 64, 175, 0.6)',
    bgSummary: 'rgba(165, 180, 252, 0.3)',
    accent: 'rgba(30, 64, 175, 0.6)',
    closeIcon: '#4767AD',
    svgActive: '#102554',
    svgMuted: '#64748b',
    dividerActive: '#a5b4fc',
    dividerMuted: 'rgba(15, 23, 42, 0.09)',
    chipBorder: '#a5b4fc',

    bgTag: '#fde7f3',
    borderTag: '#f4b0e0',
    
  },
  slice: {
    primary: '#b62196',
    primaryLight: '#f5d0ea',
    bg: '#fdf2f8',
    borderSummary: 'rgba(192, 38, 211, 0.6)',
    bgSummary: 'rgba(245, 208, 234, 0.3)',
    accent: 'rgba(192, 38, 211, 0.6)',
    closeIcon: '#CF5EB6',
    svgActive: '#b62196',
    svgMuted: '#64748b',
    dividerActive: '#f5d0ea',
    dividerMuted: 'rgba(15, 23, 42, 0.09)',

    // bgTag: '#fde7f3',
    // borderTag: '#f4b0e0',
    // chipBorder: '#e0b0d8',
  },
  emptyState: {
    bar1: '#c7d2fe',
    bar2: '#a5b4fc',
    bar3: '#818cf8',
    axis: '#e2e8f0',
  },
  lock: {
    stroke: '#64748B',
  },
  closeSvg: {
    bgFill: '#EEF2FF',
  },
};

// --- ANIMATIONS ---
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.03); }
  100% { transform: scale(1); }
`;

// --- STYLED COMPONENTS ---

export const CanvasContainer = styled(Box)(({ theme }) => `
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  min-width: 0;
  padding-left: 2px;

  ${theme.breakpoints.down('md')} {
    padding-left: 0;
  }
`);

export const FieldRow = styled(Box)(`
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 14px 20px 14.67px;
  width: 100%;
  position: relative;
  border-bottom: 0.67px solid rgba(15, 23, 42, 0.09);
`);

export const AccentBarWrapper = styled(Box)(`
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
`);

export const AccentBar = styled('span', {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'height',
})(({ color, height }) => ({
  position: 'absolute',
  left: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  width: '3px',
  height: height || '60px',
  borderRadius: '0 4px 4px 0',
  backgroundColor: color,
}));

// NOTE: the View / Filter / Slice chip ("Pill") and its icons now live in
// the shared `chips/` module (see `Chip` from '../chips') and are no longer
// defined here.

export const PillDivider = styled('span')(({ color }) => ({
  flex: '1 0 0',
  height: '1px',
  backgroundColor: color || 'rgba(15, 23, 42, 0.09)',
}));

export const DropZoneWrap = styled(Box)`
  padding-top: 10px;
  width: 100%;
  overflow: auto;
  max-height: 85px;
  padding-right: 10px;
  ${thinScrollbar.styles};
`;

export const DropZone = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'thin' && prop !== 'disabled',
})(({ thin, disabled }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  border: thin
    ? '0.667px dashed rgba(15, 23, 42, 0.5)'
    : '2px dashed rgba(15, 23, 42, 0.4)',
  borderRadius: '12px',
  padding: thin ? '8.667px 12.667px' : '10px 14px',
  color: thin ? '#64748b' : '#464749',
  fontSize: '14px',
  lineHeight: '20px',
  opacity: disabled ? 0.5 : 1,
  pointerEvents: disabled ? 'none' : 'auto',
  '& svg': {
    width: '12px',
    height: '12px',
    flexShrink: 0,
  },
  '& .dropzone-add-icon-view': {
    fontSize: '13px',
    color: '#464749',
  },
  '& .dropzone-add-icon-filter, & .dropzone-add-icon-slice': {
    fontSize: '13px',
    color: '#64748b',
  },
  overflow: 'auto',
  maxHeight: '85px',
  ...thinScrollbar.styles
}));

export const ChipTag = styled('span')(({ type }) => {
  const styles = {
    view: { bg: PANEL_COLORS.view.bg, border: PANEL_COLORS.view.primaryLight, color: PANEL_COLORS.view.primaryDark },
    filter: { bg: PANEL_COLORS.filter.bgTag, border: PANEL_COLORS.filter.borderTag, color: PANEL_COLORS.filter.primary },
    slice: { bg: PANEL_COLORS.slice.bg, border: PANEL_COLORS.slice.primaryLight, color: PANEL_COLORS.slice.primary },
  }[type];

  return {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    borderRadius: '12px',
    padding: '4.6px 8.6px',
    fontSize: '16px',
    lineHeight: '20px',
    backgroundColor: styles.bg,
    border: `0.6px solid ${styles.border}`,
    color: styles.color,
  };
});

export const RemoveButton = styled('button')(({ iconColor }) => ({
  position: 'absolute',
  top: '-4px',
  right: '-5px',
  width: '14px',
  height: '14px',
  borderRadius: '50%',
  backgroundColor: '#ffffff',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  cursor: 'pointer',
  boxShadow: '0 0 0 1px rgba(0,0,0,0.06)',
  '& svg': {
    width: '8px',
    height: '8px',
    color: iconColor,
  },
}));

export const FSPanel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bordered' && prop !== 'isMobileBorder',
})(({ theme, bordered, isMobileBorder }) => ({
  backgroundColor: '#ffffff',
  flex: '1 0 0',
  padding: '14px 21px 14px 20px',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  ...(bordered && {
    borderRight: '1px solid rgba(15, 23, 42, 0.09)',
  }),
  ...(isMobileBorder && {
    [theme.breakpoints.down('md')]: {
      borderBottom: '1px solid rgba(15, 23, 42, 0.09)',
    },
  }),
}));

export const SummaryStrip = styled(Box)(({ theme }) => `
  background-color: #f1f4f9;
  border-bottom: 0.67px solid rgba(15, 23, 42, 0.15);
  border-right: 0.67px solid rgba(15, 23, 42, 0.15);
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 8px 20px;
  width: 100%;
  flex-wrap: wrap;

  /* Keep summary strip visible on scroll */
  position: sticky;
  top: 0;
  left: 0;
  z-index: 10;
  box-shadow: 0 -6px 18px rgba(16,24,40,0.06);

  ${theme.breakpoints.down('md')} {
    flex-direction: column;
    align-items: stretch;
  }
`);

export const SummaryChip = styled('span')(({ type }) => {
  const styles = {
    viewing: {
      bg: PANEL_COLORS.view.bgSummary,
      border: PANEL_COLORS.view.borderSummary,
      color: PANEL_COLORS.view.colorSummary,
    },
    by: {
      bg: PANEL_COLORS.slice.bgSummary,
      border: PANEL_COLORS.slice.borderSummary,
      color: PANEL_COLORS.slice.primary,
    },
    for: {
      bg: PANEL_COLORS.filter.bgSummary,
      border: PANEL_COLORS.filter.borderSummary,
      color: PANEL_COLORS.filter.primary,
    },
  }[type];

  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2.667px 6.667px',
    borderRadius: '4px',
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '16px',
    whiteSpace: 'nowrap',
    backgroundColor: styles.bg,
    border: `0.667px solid ${styles.border}`,
    color: styles.color,
  };
});

export const GenerateButton = styled(Button)(({ disabled }) => ({
  backgroundColor: disabled ? '#e7e7e6' : 'var(--button, #2563eb)',
  borderRadius: '4px',
  padding: '8px 16px',
  fontSize: '14px',
  lineHeight: '20px',
  color: disabled ? '#adadac' : '#ffffff',
  whiteSpace: 'nowrap',
  textTransform: 'none',
  fontFamily: 'inherit',
  '&:hover': {
    backgroundColor: disabled ? '#e7e7e6' : '#084a9c',
  },
}));

export const EmptyIconCard = styled(Paper)`
  background-color: #ffffff;
  box-shadow:
    0 1px 1.5px rgba(0, 0, 0, 0.1),
    0 1px 1px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  padding: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  position: relative;
  animation: ${pulse} 3s infinite ease-in-out;
`;

export const FilterChipBox = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  position: 'relative',
});

export const ChipButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isError' && prop !== 'isOpen',
})(({ isError, isOpen }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  minHeight: '32px',
  height: 'auto',
  padding: '4px 12px 4px 12px',
  borderRadius: '16px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  backgroundColor: isError ? '#fce8e6' : isOpen ? PANEL_COLORS.filter.primary : '#eef2ff',
  color: isError ? '#d32f2f' : isOpen ? '#fff' : PANEL_COLORS.filter.primary,
  border: isError ? '1px solid red' : `1px solid ${PANEL_COLORS.filter.chipBorder}`,
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.2s ease',
  userSelect: 'none',
  minWidth: '80px',
  maxWidth: '450px',
  '& .dropdown-icon': {
    fontSize: '18px',
    color: isOpen ? '#fff' : isError ? '#d32f2f' : PANEL_COLORS.filter.primary,
    flexShrink: 0,
    marginLeft: 'auto',
  },
}));

export const ChipLabelText = styled('span')({
  wordBreak: 'break-word',
  whiteSpace: 'normal',
  fontWeight: '400',
  lineHeight: '18px',
  fontSize: '16px',
});

export const FilterPopover = styled(Popover)({
  '& .MuiPopover-paper': {
    marginTop: '6px',
    width: '360px',
    maxHeight: '400px',
    borderRadius: '8px',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.12)',
    padding: '8px 4px',
    border: '1px solid #eaeaea',
  },
});

export const PopoverOptionsWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
});

export const FilterFormControlLabel = styled(FormControlLabel)({
  marginRight: 0,
  marginLeft: 0,
  paddingLeft: '8px',
  paddingRight: '8px',
  paddingTop: '4px',
  paddingBottom: '4px',
  borderRadius: '4px',
  alignItems: 'flex-start',
  '&:hover': {
    backgroundColor: '#f7fafc',
  },
});

export const FilterCheckbox = styled(Checkbox)({
  color: '#0066cc',
  '&.Mui-checked': {
    color: '#0066cc',
  },
  paddingTop: '2px',
  paddingBottom: '2px',
});

export const FilterOptionLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isChecked',
})(({ isChecked }) => ({
  fontSize: '13px',
  color: '#2d3748',
  fontWeight: isChecked ? 500 : 400,
  wordBreak: 'break-word',
  whiteSpace: 'normal',
}));

export const NoOptionsText = styled(Typography)({
  padding: '8px',
  fontSize: '13px',
  color: '#a0aec0',
});

export const PanelHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fullWidth' && prop !== 'gap',
})(({ fullWidth, gap }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: gap || '8px',
  flexShrink: 0,
  ...(fullWidth && { width: '100%' }),
}));

export const SelectedCountText = styled(Typography)({
  fontSize: '13px',
  color: '#000',
});

export const ClearAllButton = styled(Button)({
  padding: 0,
  minWidth: 'auto',
  fontSize: '13px',
  color: '#000',
  textDecoration: 'underline',
  textTransform: 'none',
});

export const LockButton = styled(IconButton)({
  padding: 0,
});

export const ChipsScrollContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isScrollable',
})(({ isScrollable }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: isScrollable ? '12px' : '8px',
  paddingTop: '8px',
  minHeight: '32px',
  maxHeight: '85px',

  ...(isScrollable && {
    overflow: 'auto',
    width: 'calc(100% - 5px)',
  }),
  
  paddingRight: '5px',
  overflow: 'auto',
  ...thinScrollbar.styles,
}));


export const SummaryContentBox = styled(Box)({
  flex: 1,
  minWidth: '220px',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '6px',
  fontSize: '13px',
  color: '#000',
});

export const SummarySectionBox = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '4px',
});

export const InstructionText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isHeadline',
})(({ isHeadline }) => ({
  fontSize: isHeadline ? '14px' : '13px',
  color: isHeadline ? '#0f172a' : '#000',
  ...(isHeadline && {
    maxWidth: '384px',
    lineHeight: '20px',
  }),
}));

export const EmptyStateContainer = styled(Box)({
  display: 'flex',
  flex: '1 0 0',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: '80px',
  paddingBottom: '80px',
  paddingLeft: '24px',
  paddingRight: '24px',
  // height: 'calc(100% - 320px)',
  textAlign: 'center',
  // overflow: 'auto',
});

export const EmptyStateIconBadge = styled(Box)({
  position: 'absolute',
  bottom: '-10px',
  right: '-10px',
  width: '28px',
  height: '28px',
  backgroundColor: '#2563eb',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 1px 1.5px rgba(0,0,0,0.1)',
  '& .badge-add-icon': {
    color: '#fff',
    fontSize: '16px',
  },
});

export const ErrorAlert = styled(Alert)({
  marginBottom: '16px',
  borderRadius: '4px',
  backgroundColor: '#fde8e8',
  color: '#1a1a1a',
  fontSize: '13px',
  fontWeight: 400,
  border: '1px solid #f8b4b4',
  paddingTop: '4px',
  paddingBottom: '4px',
  '& .MuiAlert-message': {
    padding: '4px 0',
  },
  '& .error-alert-icon': {
    color: '#d32f2f',
    fontSize: '18px',
  },
});

export const ErrorMessageSpan = styled('span')({
  fontStyle: 'italic',
});

// --- STYLING SVG ELEMENTS FROM STYLE FILE ---

export const EmptyStateSvg = styled('svg')({
  '& .bar-1': { fill: PANEL_COLORS.emptyState.bar1 },
  '& .bar-2': { fill: PANEL_COLORS.emptyState.bar2 },
  '& .bar-3': { fill: PANEL_COLORS.emptyState.bar3 },
  '& .axis-line': { stroke: PANEL_COLORS.emptyState.axis },
});

export const LockSvgIcon = styled('svg')({
  '& path': {
    stroke: PANEL_COLORS.lock.stroke,
  },
});

export const CloseSvg = styled('svg', {
  shouldForwardProp: (prop) => prop !== 'type',
})(({ type }) => {
  const typeClr = PANEL_COLORS[type]?.closeIcon || PANEL_COLORS.view.closeIcon;

  return {
    position: 'absolute',
    right: '-6px',
    top: '-4px',
    cursor: 'pointer',
    zIndex: 0,
    '& .close-bg-path': {
      fill: PANEL_COLORS.closeSvg.bgFill,
    },
    '& .close-svg-path': {
      fill: typeClr,
    },
  };
});